import { escapeXml } from '../xmlHelpers';

export { escapeXml };

export const API_BASE = window.location.hostname === 'localhost' 
  ? 'http://localhost:3001' 
  : 'https://doclightning.askrichard.io';

// Helper to broadcast authenticated userId from API responses
export const broadcastUserId = (responseData) => {
  if (responseData?.responseUserId) {
    window.dispatchEvent(new CustomEvent('intacct-auth-userid', { 
      detail: { 
        userId: responseData.responseUserId,
        companyId: responseData.responseCompanyId || ''
      } 
    }));
  }
};

// Build the authentication XML fragment used in every Intacct request
export const buildAuthXml = (config) => {
  return config.useSessionId && config.sessionId 
    ? `<sessionid>${config.sessionId}</sessionid>` 
    : `<login><userid>${config.userId}</userid><companyid>${config.companyId}</companyid><password>${config.userPassword}</password>${config.locationId ? `<locationid>${config.locationId}</locationid>` : ''}</login>`;
};

// Wrap function XML in a complete Intacct request envelope
export const buildXmlPayload = (config, functionsXml) => {
  const authXml = buildAuthXml(config);
  return `<?xml version="1.0" encoding="UTF-8"?><request><control><senderid>${config.senderId}</senderid><password>${config.senderPassword}</password><controlid>${new Date().getTime()}</controlid><uniqueid>false</uniqueid><dtdversion>3.0</dtdversion><includewhitespace>false</includewhitespace></control><operation><authentication>${authXml}</authentication><content>${functionsXml}</content></operation></request>`;
};

// Send an xmlrequest payload to the Intacct API via our backend proxy
export const sendXmlRequest = async (config, objectName, xmlPayload, logLabel = '') => {
  if (logLabel) console.log(`=== ${logLabel} XML ===\n`, xmlPayload, '\n=== END XML ===');
  
  const response = await fetch(`${API_BASE}/api/query`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...config, object: objectName, xmlrequest: xmlPayload })
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Server returned ${response.status}: ${response.statusText}. Details: ${errorText}`);
  }
  
  const result = await response.json();
  broadcastUserId(result);
  return result;
};

// Helper to extract error message from an Intacct result object
export const extractErrorMessage = (res) => {
  if (res.status === 'success') return null;
  if (res.errormessage) {
    const err = res.errormessage.error;
    if (Array.isArray(err)) {
      return err[0]?.description2 || err[0]?.description || err[0]?.errorno || "Failed";
    } else if (err) {
      return err.description2 || err.description || err.errorno || "Failed";
    } else if (typeof res.errormessage === 'string') {
      return res.errormessage;
    }
  }
  return "Failed";
};

// Send a batch of <function> elements in a single Intacct request
export const sendIntacctBatch = async (config, functions, objectName) => {
  if (functions.length === 0) return [];
  const xmlPayload = buildXmlPayload(config, functions.join(''));
  const result = await sendXmlRequest(config, objectName, xmlPayload, `${objectName} BATCH`);
  return result.results || [];
};

// ============ DATE FORMATTING HELPER ============
export const formatDateXml = (dateVal, tagName) => {
  if (!dateVal && dateVal !== 0) return `<${tagName}><year></year><month></month><day></day></${tagName}>`;
  
  try {
    let date;
    
    if (typeof dateVal === 'number') {
      const excelEpoch = new Date(1899, 11, 30);
      date = new Date(excelEpoch.getTime() + dateVal * 24 * 60 * 60 * 1000);
    } 
    else if (dateVal instanceof Date) {
      date = dateVal;
    }
    else if (typeof dateVal === 'string') {
      const str = dateVal.trim();
      if (str.includes('/')) {
        const parts = str.split('/');
        if (parts.length === 3) {
          const month = parseInt(parts[0], 10);
          const day = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);
          date = new Date(year, month - 1, day);
        }
      } else if (str.includes('-')) {
        date = new Date(str);
      } else {
        date = new Date(str);
      }
    }
    
    if (!date || isNaN(date.getTime())) {
      console.log(`Could not parse date value: ${dateVal} (type: ${typeof dateVal})`);
      return `<${tagName}><year></year><month></month><day></day></${tagName}>`;
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `<${tagName}><year>${year}</year><month>${month}</month><day>${day}</day></${tagName}>`;
  } catch (e) {
    console.error(`Date parsing error for ${dateVal}:`, e);
    return `<${tagName}><year></year><month></month><day></day></${tagName}>`;
  }
};
